/**
 * Facebook Conversion API fbc 参数值验证正则表达式
 *
 * fbc 格式: fb.subdomainIndex.creationTime.fbclid
 * - version: 固定为 "fb"
 * - subdomainIndex: 域名索引数字
 * - creationTime: UNIX 时间戳(毫秒)
 * - fbclid: Facebook 点击 ID
 */

import logger from './client-logger';

// 基础正则表达式 - 匹配标准格式
export const fbcBasicRegex = /^fb\.\d\.\d+\.[a-zA-Z0-9_-]+$/;

// 严格正则表达式 - 对各部分进行更严格的验证
export const fbcStrictRegex = /^fb\.([0-9]{1,2})\.([0-9]{13})\.([a-zA-Z0-9_-]{10,})$/;

/**
 * FBC 解析后的结构
 */
export interface FbcParsedValue {
  version: string;
  subdomainIndex: number;
  creationTime: number;
  fbclid: string;
  isExpired: boolean;
}

/**
 * 验证 fbc 参数值是否符合格式要求
 * @param fbcValue - 要验证的 fbc 参数值
 * @param strict - 是否使用严格验证
 * @param checkExpiration - 是否检查时间戳是否过期（超过88天）
 * @returns 是否符合格式
 */
export function validateFbc(
  fbcValue: string | null | undefined,
  strict = false,
  checkExpiration = false,
): boolean {
  if (!fbcValue) return false;

  const regex = strict ? fbcStrictRegex : fbcBasicRegex;
  const isValidFormat = regex.test(fbcValue);

  // 如果格式无效或不需要检查过期时间，直接返回格式验证结果
  if (!isValidFormat || !checkExpiration) return isValidFormat;

  // 检查时间戳是否过期（超过88天）
  try {
    const parts = fbcValue.split('.');
    if (parts.length !== 4) return false;

    const creationTime = parseInt(parts[2], 10);
    const currentTime = Date.now();
    const maxAge = 88 * 24 * 60 * 60 * 1000; // 88天的毫秒数

    // 如果时间戳距离当前时间超过88天，则视为无效
    return currentTime - creationTime <= maxAge;
  } catch (e) {
    logger.log(e, 'validateFbc');
    return false;
  }
}

/**
 * 从 fbc 值中提取各个组成部分
 * @param fbcValue - fbc 参数值
 * @param checkExpiration - 是否检查时间戳是否过期（超过88天）
 * @returns 包含各个部分的对象，如果格式不正确或过期则返回 null
 */
export function parseFbc(
  fbcValue: string | null | undefined,
  checkExpiration = false,
): FbcParsedValue | null {
  if (!fbcValue || !validateFbc(fbcValue, false, checkExpiration)) return null;

  const parts = fbcValue.split('.');
  if (parts.length !== 4) return null;

  return {
    version: parts[0],
    subdomainIndex: parseInt(parts[1], 10),
    creationTime: parseInt(parts[2], 10),
    fbclid: parts[3],
    isExpired: checkExpiration ? isExpired(parseInt(parts[2], 10)) : false,
  };
}

/**
 * 检查时间戳是否已过期（超过88天）
 * @param timestamp - 要检查的时间戳（毫秒）
 * @returns 是否已过期
 */
export function isExpired(timestamp: number): boolean {
  const currentTime = Date.now();
  const maxAge = 88 * 24 * 60 * 60 * 1000; // 88天的毫秒数
  return currentTime - timestamp > maxAge;
}

/**
 * 获取有效的 fbc 值（过滤掉过期的值）
 * @param fbcValue - 要验证的 fbc 参数值
 * @returns 有效的 fbc 值，如果无效或过期则返回 null
 */
export function getValidFbc(fbcValue: string | null | undefined): string | null {
  if (!fbcValue) return null;

  // 检查格式和过期时间
  if (!validateFbc(fbcValue)) {
    return null;
  }

  return fbcValue;
}
